import React, { useState } from 'react';
import { Paper, Group, Avatar, Text, Stack, Button, Box, Divider } from '@mantine/core';
import { formatDistanceToNow } from 'date-fns';
import CommentsForm from './CommentsForm';
import { CommentData, CommentFormData } from '../../types/comment';

interface CommentItemProps {
  comment: CommentData;
  postId: number;
  isUserSignedIn: boolean;
  onCommentSubmit: (formData: CommentFormData) => Promise<void>;
  nestingLevel?: number;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  postId,
  isUserSignedIn,
  onCommentSubmit,
  nestingLevel = 0,
}) => {
  const [isReplying, setIsReplying] = useState(false);
  const [showReplies, setShowReplies] = useState(true);

  // Maximum nesting level to prevent excessive indentation
  const MAX_NESTING_LEVEL = 3;
  const currentNestingLevel = Math.min(nestingLevel, MAX_NESTING_LEVEL);

  const handleReplyClick = () => {
    setIsReplying(!isReplying);
  };

  const handleSubmitReply = async (formData: CommentFormData) => {
    try {
      await onCommentSubmit({
        ...formData,
        parent_id: comment.id,
      });
      setIsReplying(false);
    } catch (error) {
      console.error('Failed to submit reply:', error);
    }
  };

  const toggleReplies = () => {
    setShowReplies(!showReplies);
  };

  // Format date (e.g., "2 days ago")
  const formattedDate = formatDistanceToNow(new Date(comment.created_at), {
    addSuffix: true,
  });

  const hasReplies = comment.comments && comment.comments.length > 0;

  return (
    <Box>
      <Paper
        shadow="lg"
        p={20}
        my="4px"
        radius="md"
        style={{
          width: '100%',
          margin: '0 auto',
          backgroundColor: '#17171790',
        }}
      >
        {/* User Info */}
        <Group justify="apart">
          <Group gap="sm">
            <Avatar src={comment.user.avatar_url} alt={comment.user.name} radius="xl" size="md" />
            <Stack gap={0}>
              <Text fw={600} size="sm" c="orange">
                {comment.user.name}
              </Text>
              <Text size="xs" c="dimmed" style={{ fontSize: 12 }}>
                {formattedDate}
              </Text>
            </Stack>
          </Group>
        </Group>

        {/* Comment Body */}
        <Text size="sm" className="mt-2 text-white">
          {comment.body}
        </Text>

        {/* Reply Button - Now positioned at the bottom right */}
        {isUserSignedIn && (
          <Group justify="right" className="mt-2">
            <Button variant="light" size="md" color="orange" onClick={handleReplyClick}>
              {isReplying ? 'Cancel' : 'Reply'}
            </Button>
          </Group>
        )}

        {/* Reply Form */}
        {isReplying && (
          <Box mt="md">
            <CommentsForm
              postId={postId}
              onCommentSubmit={handleSubmitReply}
              placeholder="Write your reply..."
              submitLabel="Reply"
              isReply={true}
            />
          </Box>
        )}

        {/* Replies Toggle Button */}
        {hasReplies && (
          <Button variant="subtle" onClick={toggleReplies} size="xs" leftSection={showReplies ? '▼' : '►'} mt="xs">
            {showReplies ? 'Hide' : 'Show'} {comment.comments!.length}{' '}
            {comment.comments!.length === 1 ? 'reply' : 'replies'}
          </Button>
        )}
      </Paper>

      {/* Nested Replies */}
      {hasReplies && showReplies && (
        <Box
          ml={30}
          mt="xs"
          pl="md"
          style={(theme) => ({
            borderLeft: `2px solid ${theme.colors.gray[6]}`,
          })}
        >
          <Stack>
            {comment.comments!.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                postId={postId}
                isUserSignedIn={isUserSignedIn}
                onCommentSubmit={onCommentSubmit}
                nestingLevel={currentNestingLevel + 1}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default CommentItem;
